import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { drivers, registry, type DriverRow, type InviteBody } from "@/api/client";
import { useConfirm } from "@/components/ConfirmDialog";
import { Field } from "@/components/Field";
import { KV } from "@/components/KV";
import { StatusChip, type StatusMap } from "@/components/StatusChip";
import { SearchInput, Select } from "@/components/inputs";
import { ago, dateTime } from "@/lib/format";
import { ImageField } from "@/components/ImageCapture";
import { useMutationFeedback } from "@/lib/hooks/useMutationFeedback";

export const DRIVER_CHIP: StatusMap = {
  invited: { label: "INVITED", color: "var(--info)" },
  active: { label: "ACTIVE", color: "var(--ok)" },
  blocked: { label: "BLOCKED", color: "var(--bad)" },
  expired: { label: "EXPIRED", color: "var(--warn)" },   // D-65: invite >30d unverified
  removed: { label: "REMOVED", color: "var(--t-mute)" },
};

export function DriverChip({ st }: { st: string }) {
  return <StatusChip st={st} map={DRIVER_CHIP} />;
}

/** Read-only driver telemetry block — Handset, Phone health, Recent app
 *  events, Sessions, Assets held. ONE implementation shared by the Overview
 *  drawer and the Edit page's info section (UI commonalization law). */
const _sz = (mb: unknown) => typeof mb !== "number" ? "—"
  : mb >= 10240 ? `${(mb / 1024).toFixed(0)} GB` : mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`;
// extras rendered into dedicated rows below; anything the app adds later
// falls through to the generic "More" line instead of disappearing
const _KNOWN_EXTRAS = new Set(["sdk_int", "security_patch", "app_code", "build_type",
  "ram_mb", "storage_mb", "abi", "screen", "low_ram", "play_services", "lang"]);

/** Tiny clipboard button with a transient "copied" confirmation. */
function CopyBtn({ payload }: { payload: () => string }) {
  const [ok, setOk] = useState(false);
  return (
    <button className="btn ghost sm" style={{ padding: "2px 10px", fontSize: 11 }}
      onClick={() => { void navigator.clipboard?.writeText(payload());
        setOk(true); window.setTimeout(() => setOk(false), 1800); }}>
      {ok ? "copied ✓" : "Copy"}
    </button>
  );
}

export function DriverInfoPanel({ d }: { d: DriverRow }) {
  const held = useQuery({
    queryKey: ["drivers", "assets", d.driver_id],
    queryFn: () => registry.driverAssets(d.driver_id),
  });
  const ex = d.handset?.extra ?? {};
  const more = Object.entries(ex).filter(([k]) => !_KNOWN_EXTRAS.has(k));
  return (<>
    <div className="section-label tight">Handset</div>
    {d.handset ? (
      <>
        <KV k="Phone" v={d.handset.name ? `${d.handset.name} · ${d.handset.model}` : (d.handset.model ?? "—")} mono={false} />
        <KV k="OS" mono={false}
          v={[d.handset.os ?? "—", ex.sdk_int != null ? `API ${ex.sdk_int}` : null,
              ex.security_patch ? `patch ${ex.security_patch}` : null].filter(Boolean).join(" · ")} />
        <KV k="App" mono={false}
          v={[`${d.handset.app_ver ?? "—"}${ex.app_code != null ? ` (build ${ex.app_code})` : ""}`,
              ex.build_type ? String(ex.build_type) : null].filter(Boolean).join(" · ")} />
        <KV k="BLE" v={d.handset.ble ?? "—"} mono={false} />
        {(ex.ram_mb != null || ex.storage_mb != null || ex.abi || ex.screen) && (
          <KV k="Hardware" mono={false}
            v={[ex.ram_mb != null ? `${_sz(ex.ram_mb)} RAM${ex.low_ram ? " (low-RAM device)" : ""}` : null,
                ex.storage_mb != null ? `${_sz(ex.storage_mb)} storage` : null,
                ex.abi ? String(ex.abi) : null,
                ex.screen ? String(ex.screen) : null].filter(Boolean).join(" · ")} />
        )}
        {ex.play_services && <KV k="Play services" v={String(ex.play_services)} mono={false} />}
        {ex.lang && <KV k="Locale" v={String(ex.lang)} mono={false} />}
        {more.length > 0 && (
          <KV k="More" mono={false}
            v={more.map(([k, v]) => `${k} ${String(v)}`).join(" · ")} />
        )}
      </>
    ) : (
      <div className="cap">
        Unknown — reported by the app at login from version 1.1 up.
      </div>
    )}

    {d.phone_health && (() => {
      const h = d.phone_health as Record<string, unknown> & { at: string };
      const link = h.link as { state?: string; asset_id?: string; rssi?: number } | undefined;
      const flags = [h.charging ? "charging" : null, h.power_save ? "power-save" : null,
        h.notif_enabled === false ? "notifications OFF" : null,
        h.thermal && h.thermal !== "none" ? `thermal ${h.thermal}` : null].filter(Boolean).join(" · ");
      const ofTotal = (free: unknown, total: unknown) =>
        `${_sz(free)} free${typeof total === "number" ? ` of ${_sz(total)}` : ""}`;
      return (<>
        <div className="section-label tight">Phone health</div>
        <KV k="Battery" v={`${h.battery_pct ?? "—"}%${flags ? ` · ${flags}` : ""}`} mono={false} />
        <KV k="Network" v={`${h.net ?? "—"}${h.metered ? " (metered)" : ""}`} mono={false} />
        {(h.ram_free_mb != null || h.low_mem != null) && (
          <KV k="RAM" v={`${ofTotal(h.ram_free_mb, ex.ram_mb)}${h.low_mem ? " · LOW MEMORY" : ""}`} mono={false} />
        )}
        {h.storage_free_mb != null && (
          <KV k="Storage free" v={ofTotal(h.storage_free_mb, ex.storage_mb)} mono={false} />
        )}
        {link && <KV k="BLE link" v={`${link.state ?? "—"}${link.asset_id ? ` · ${link.asset_id}` : ""}${link.rssi != null ? ` · ${link.rssi} dBm` : ""}`} mono={false} />}
        <KV k="Reported" v={ago(h.at)} mono={false} />
      </>);
    })()}

    {(d.phone_events?.length ?? 0) > 0 && (<>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <div className="section-label tight">Recent app events</div>
        <CopyBtn payload={() => JSON.stringify(d.phone_events, null, 2)} />
      </div>
      {d.phone_events!.map((e, i) => {
        const dd = (e.data ?? {}) as Record<string, unknown>;
        let when = ago(e.at);
        let line: string;
        if (e.type === "app_exit") {
          // the DEATH is the event — key by died_at, not the upload time
          if (dd.died_at) when = ago(String(dd.died_at));
          const rss = Number(dd.rss_kb);
          line = ["app exit", dd.reason ? String(dd.reason) : null,
            dd.importance && dd.importance !== "other" ? String(dd.importance) : null,
            Number.isFinite(rss) && rss > 0 ? _sz(Math.round(rss / 1024)) : null,
            dd.desc ? String(dd.desc).slice(0, 36) : null].filter(Boolean).join(" · ");
        } else if (e.type === "crash") {
          line = ["crash", dd.exc ? String(dd.exc) : null,
            dd.msg ? String(dd.msg).slice(0, 48) : null,
            dd.low_mem ? "LOW MEMORY" : null,
            dd.ram_free_mb != null ? `${dd.ram_free_mb} MB free` : null].filter(Boolean).join(" · ");
        } else {
          line = `${e.type}${e.data ? ` · ${Object.entries(e.data).map(([k, v]) => `${k}=${String(v)}`).join(" ")}` : ""}`;
        }
        return <KV key={i} k={when} v={line} mono={false} />;
      })}
    </>)}

    <div className="section-label tight">Sessions</div>
    {(d.sessions?.length ?? 0) === 0 && (
      <div className="cap">
        None — the driver hasn't verified on the app yet.
      </div>
    )}
    {d.sessions?.map((sx, i) => (
      <KV key={i} k={sx.revoked_at ? "revoked" : "session"}
        v={`${sx.bound ? "handset-bound" : "unbound"} · since ${dateTime(sx.created_at)} · seen ${ago(sx.last_seen)}${sx.device_model ? ` · ${sx.device_model}` : ""}${sx.app_ver ? ` · app ${sx.app_ver}` : ""}`}
        accent={sx.revoked_at ? "var(--t-mute)" : "var(--ok)"} />
    ))}

    <div className="section-label tight">Assets held</div>
    {(held.data?.assets.length ?? 0) === 0 ? (
      <div className="cap">
        None — allocate one from the asset's drawer (Registry → Inventory → Assets → Driver).
      </div>
    ) : held.data!.assets.map((a) => (
      <KV key={a.asset_id} k={a.asset_type}
        v={`${a.asset_id}${a.label ? ` · ${a.label}` : ""} · since ${dateTime(a.since)}`} />
    ))}
  </>);
}

/* ---------------- phone input (country code + local number) ---------------- */

/** India-first: +91 default, common codes for the rest. The stored msisdn is
 *  always full E.164 (cc + digits) — the split exists only in the UI. */
export const COUNTRY_CODES = [
  { cc: "+91", label: "IN +91" },
  { cc: "+880", label: "BD +880" },
  { cc: "+94", label: "LK +94" },
  { cc: "+977", label: "NP +977" },
  { cc: "+971", label: "AE +971" },
  { cc: "+966", label: "SA +966" },
  { cc: "+65", label: "SG +65" },
  { cc: "+62", label: "ID +62" },
  { cc: "+44", label: "GB +44" },
  { cc: "+1", label: "US +1" },
];

export function PhoneInput({ cc, num, onCc, onNum, autoFocus }: {
  cc: string; num: string;
  onCc: (v: string) => void; onNum: (v: string) => void;
  autoFocus?: boolean;
}) {
  return (
    <div style={{ display: "flex", gap: 6 }}>
      <Select value={cc} onChange={(e) => onCc(e.target.value)}
        style={{ width: 110, minWidth: 0, flexShrink: 0 }}>
        {COUNTRY_CODES.map((c) => <option key={c.cc} value={c.cc}>{c.label}</option>)}
      </Select>
      <SearchInput mono placeholder="9876543210" value={num} autoFocus={autoFocus}
        onChange={(e) => onNum(e.target.value.replace(/[^0-9]/g, ""))}
        style={{ flex: 1, width: "100%", minWidth: 0 }} />
    </div>
  );
}

/** Compose the E.164 msisdn the backend expects. */
export function fullMsisdn(cc: string, num: string): string {
  return `${cc}${num.trim()}`;
}

/* ---------------- CSV (bulk invite) ---------------- */

/** Split one CSV line honouring double quotes — addresses contain commas. */
function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "", inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQ) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; }   // escaped quote
        else inQ = false;
      } else cur += ch;
    } else if (ch === '"') inQ = true;
    else if (ch === ",") { out.push(cur.trim()); cur = ""; }
    else cur += ch;
  }
  out.push(cur.trim());
  return out;
}

/** Columns: name,msisdn,current_address,current_pin,permanent_address,
 *  permanent_pin,dl_number — empty permanent columns copy the current
 *  address (the "same as" convention for sheets). */
export function parseCsv(text: string): InviteBody[] {
  return text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean)
    .filter((l, i) => !(i === 0 && /name/i.test(l) && /(msisdn|phone|number)/i.test(l)))
    .map((l) => {
      const c = splitCsvLine(l);
      const [name = "", msisdn = "", curA = "", curP = "", permA = "", permP = "", dl = ""] = c;
      const samePerm = !permA && !permP;
      return {
        name, msisdn,
        temp_address: curA || null, temp_pincode: curP || null,
        perm_address: (samePerm ? curA : permA) || null,
        perm_pincode: (samePerm ? curP : permP) || null,
        dl_number: dl || null,
      };
    })
    .filter((r) => r.name && r.msisdn);
}

export const CSV_TEMPLATE = "data:text/csv;charset=utf-8," + encodeURIComponent(
  'name,msisdn,current_address,current_pin,permanent_address,permanent_pin,dl_number\n' +
  'Ravi Kumar,9876543210,"12, MG Road, Thane",400601,"Village X, Satara",415001,MH1220220012345\n');

/* ---------------- Edit-page sections ---------------- */

/** KYC editor (MVP: addresses + DL — Aadhaar arrives only as a
 *  provider-verified attestation later, never a typed field). */
export function KycSection({ d, onSaved }: { d: DriverRow; onSaved: () => void }) {
  const fb = useMutationFeedback();
  const [f, setF] = useState({
    temp_address: "", temp_pincode: "", perm_address: "", perm_pincode: "", dl_number: "",
  });
  useEffect(() => {
    const k = d.kyc;
    setF({
      temp_address: k?.temp_address ?? "", temp_pincode: k?.temp_pincode ?? "",
      perm_address: k?.perm_address ?? "", perm_pincode: k?.perm_pincode ?? "",
      dl_number: k?.dl_number ?? "",
    });
  }, [d.driver_id, d.kyc]);

  const save = useMutation({
    mutationFn: () => drivers.saveKyc(d.driver_id, {
      temp_address: f.temp_address || null, temp_pincode: f.temp_pincode || null,
      perm_address: f.perm_address || null, perm_pincode: f.perm_pincode || null,
      dl_number: f.dl_number || null,
    }),
    onSuccess: () => { fb.ok(); onSaved(); },
    onError: fb.fail,
  });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setF((s) => ({ ...s, [k]: e.target.value }));

  return (
    <>
      <Field label="Driving licence no.">
        <SearchInput mono placeholder="MH12 20220012345" value={f.dl_number}
          onChange={set("dl_number")} style={{ width: "100%" }} />
      </Field>
      {d.kyc?.dl_number && (
        <div style={{ fontSize: 11.5, color: d.kyc.dl_verified_at ? "var(--ok)" : "var(--t-mute)", margin: "4px 0 8px" }}>
          {d.kyc.dl_verified_at ? `✓ verified ${dateTime(d.kyc.dl_verified_at)}` : "not verified yet (Sarathi check comes later)"}
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,110px)", gap: 8, marginTop: 8 }}>
        <Field label="Current address">
          <SearchInput className="fill" value={f.temp_address} onChange={set("temp_address")} />
        </Field>
        <Field label="PIN">
          <SearchInput className="fill" mono value={f.temp_pincode} onChange={set("temp_pincode")} />
        </Field>
        <Field label="Permanent address">
          <SearchInput className="fill" value={f.perm_address} onChange={set("perm_address")} />
        </Field>
        <Field label="PIN">
          <SearchInput className="fill" mono value={f.perm_pincode} onChange={set("perm_pincode")} />
        </Field>
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "flex-end", marginTop: 10 }}>
        {fb.errorNode}
        {d.kyc && (
          <span style={{ fontSize: 11, color: "var(--t-dim)" }}>
            last saved {ago(d.kyc.updated_at)} by {d.kyc.updated_by ?? d.kyc.source}
          </span>
        )}
        <button className="btn ghost sm" disabled={save.isPending} onClick={() => save.mutate()}>
          Save KYC
        </button>
      </div>
    </>
  );
}

/** Number change: the person (ID, KYC, history) stays; the PHONE identity
 *  is re-pointed and the driver re-verifies on the app (drops to INVITED). */
export function ChangeNumber({ d, onDone }: { d: DriverRow; onDone: () => void }) {
  const fb = useMutationFeedback();
  const [cc, setCc] = useState("+91");
  const [num, setNum] = useState("");
  const { ask, dialog } = useConfirm();
  const mut = useMutation({
    mutationFn: () => drivers.changeNumber(d.driver_id, fullMsisdn(cc, num)),
    onSuccess: () => { fb.ok(); setNum(""); onDone(); },
    onError: fb.fail,
  });
  if (d.status === "removed") return null;
  return (
    <>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <PhoneInput cc={cc} num={num} onCc={setCc} onNum={setNum} />
        </div>
        <button className="btn ghost sm" disabled={!num.trim() || mut.isPending}
          onClick={() => ask(
            <>Change <b>{d.name}</b>'s number to <b>{fullMsisdn(cc, num)}</b>? Their sessions are
              revoked and they must verify the new number on the app (back to INVITED).</>,
            () => mut.mutate(), "Change number")}>
          Change
        </button>
        {fb.errorNode}
      </div>
      <p style={{ fontSize: 11.5, color: "var(--t-dim)", marginTop: 8 }}>
        Driver ID, KYC, and history stay with the person — only the phone identity changes; the old
        number's record is kept for audit.
      </p>
      {dialog}
    </>
  );
}


/* ---------------- photo capture ---------------- */

/** Webcam capture with file-upload fallback. getUserMedia works on every
 *  modern laptop browser over HTTPS; no camera / permission denied -> the
 *  file input still gets a photo in. Frames are downscaled to <=800px JPEG
 *  client-side (~100-200 KB) before upload. */
export { CameraCapture } from "@/components/ImageCapture";

/** Edit-page photo panel: current photo + capture/retake + upload. */
export function PhotoSection({ d, onSaved }: { d: DriverRow; onSaved: () => void }) {
  const save = useMutation({
    mutationFn: (b: Blob) => drivers.uploadPhoto(d.driver_id, b),
    onSuccess: onSaved,
  });
  if (d.status === "removed") return null;
  // ImageField owns the staging AND revokes its object URL. This used to be a
  // hand-copy of it that minted a new URL every render and never revoked.
  return (
    <ImageField
      size={140}
      src={d.photo ? drivers.photoUrl(d.driver_id, d.photo.captured_at) : null}
      meta={d.photo ? `${new Date(d.photo.captured_at).toLocaleDateString()} · ${d.photo.captured_by}` : undefined}
      empty="no photo"
      onUpload={(b) => save.mutateAsync(b)}
    />
  );
}
